import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * AI Impact — digitopia.co's `/ai-impact/` landing page, rebuilt here.
 *
 * The pilot of Fatih's missing-sites plan (v1.2, D.1), 2026-10-02: the page the
 * package marks as missing outright (`01_oncelikli/ai-impact`). A **written
 * page** in this site's own section vocabulary, the way the AIMI solution page
 * is, not an imported legacy page.
 *
 * **It is a draft** (`route.draft`): built and reviewable on preview, absent
 * from www, the sitemap and the menu until Fatih approves it. The commit that
 * removes `draft` also points `/ai-impact` here — today that address 301s to
 * Enterprise AI Solutions, and the redirect check refuses a target that does not
 * exist in production, so the two have to move together.
 *
 * WHERE EACH PART COMES FROM
 *
 *  * **Copy: `content.md`, unchanged**, in its own order and heading levels.
 *    The hero is the exception: content.md starts at the logo row, so the
 *    eyebrow, the headline, the two paragraphs and the button are read out of
 *    `page.html` (`#page_header`), word for word.
 *  * **Title and description: `meta.json`, verbatim.** The title drops
 *    WordPress's own " - Digitopia" tail because this site appends its own to
 *    every title; the description is the old one exactly, including where
 *    WordPress cut it off mid-sentence.
 *  * **Pictures.** The package carries seven content pictures. Five were
 *    already on this site, compared by content rather than by name, and are
 *    reused rather than committed twice (brief §3): the hero is
 *    `boardroom-impact-platform-applause` (package
 *    `digital-transformation-platform.png`, same 1800x1200 frame), "Capture AI
 *    Value" is `aimi-assessment-presentation`
 *    (`Artificial-Intelligence-AI-benchmark-and-score.png`, same 1800x1200),
 *    the PROVEN mark and Johann Heaven's portrait are the Hall of Fame's own
 *    files (`Untitled-design-34.png`, `PROVEN-2.png`), and the AIMI wheel is
 *    the site's current `aimi-framework-wheel` rather than the package's older
 *    orange drawing of the same six dimensions. Two are new and went through
 *    `pnpm images`: `man-frustrated-at-laptop` (`lp-pain.png`) and
 *    `team-reviewing-aimi-benchmark` (`Artificial-Intelligence-Transformation.png`).
 *    The eight customer marks are the wall's own files.
 *
 * FATIH'S FOUR DECISIONS (v1.2, D.1), AS APPLIED
 *
 *  1. "Start Free Trial" and "Try the Impact Platform for Free" are **Request a
 *     Demo → `#contact`**, the AIMI page's own button. The old page ended on a
 *     HubSpot trial form; on this site the shared contact form closes every
 *     page (`components/ContactBand.tsx`), so the closing section keeps its
 *     words and carries no button pointing at a form the reader can already
 *     see — the AIMI page's reasoning of 2026-08-30.
 *  2. **The 100% / 15+ / 35+ figures are not carried over** and none are
 *     invented. The figures block prints the company-reach set the Home page
 *     prints — 300+ customers, 40+ countries, 20+ industries, 3,000+
 *     measurements — Fatih's choice (a) of 2026-10-02.
 *  3. **The six dimensions are six cards, stacked** — the AIMI page's `cta`
 *     card, one per dimension — not a new tab component. The dimension name
 *     (the old tab label) is the card's heading and the old panel heading is
 *     the line under it.
 *  4. **English only.** There is no `pages/tr/ai-impact.ts`, and since
 *     2026-10-08 there is no `/tr/ai-impact` either (`locales` below): Fatih
 *     answered *"/tr/ai-impact → /en/ai-impact 301: evet"*. Until then every
 *     written route was emitted in every locale, so preview served this page in
 *     English under `/tr/`, self-canonical and indexable — a duplicate. The 301
 *     itself is a row in `src/redirects.moved.ts` and lands in the commit that
 *     removes `draft`, with the `/ai-impact` row: `verify-redirects.mjs` refuses
 *     a redirect into a draft on every build.
 *
 * FATIH'S PILOT REVIEW (2026-10-02, 19:44 TRT), AS APPLIED
 *
 *  * **"Try" → "Discover"**, in the hero's eyebrow and in the closing
 *    sentence, and nowhere else — no trial is on offer, so the page does not
 *    promise one. Every other word stays content.md's.
 *  * **The figures (a)**, as above.
 *  * **Both amber notes are gone** — the hero's "Try" note and the figures
 *    TODO — because both are answered. `scripts/verify-drafts.mjs` now fails a
 *    production build on any TODO note left on a page that is not a draft, so
 *    a page cannot go live with one.
 *  * Still a draft: the flag comes off when Fatih approves the page.
 */
export const route: RouteDef = {
  id: 'aiImpact',
  path: 'ai-impact',
  priority: 0.7,
  locales: ['en'],
  draft: true,
}

/** The six AIMI dimensions, as content.md lists them, one card each. */
const DIMENSIONS: { name: string; title: string; points: string[] }[] = [
  {
    name: 'Capabilities',
    title: 'Unlock Transformative AI Potential',
    points: [
      'Build a flexible, adaptive organization that can quickly implement AI and digital transformation strategies.',
      'Upskill employees with the necessary AI competencies to boost productivity and innovation across the business.',
      'Leverage strategic partnerships within the AI ecosystem to accelerate transformation and stay ahead of industry trends.',
    ],
  },
  {
    name: 'Governance',
    title: 'Establish Robust AI Governance',
    points: [
      'Align leadership around a unified AI vision to drive top-down support for transformation efforts.',
      'Implement robust compliance and risk management frameworks to minimize potential regulatory and operational risks.',
      'Foster transparent and responsible AI decision-making to build trust with stakeholders and ensure ethical business operations.',
    ],
  },
  {
    name: 'Technology',
    title: 'Integrate AI-Optimized Technologies',
    points: [
      'Modernize systems and enhance data management capabilities to support seamless AI integration across business operations.',
      'Implement AI-powered analytics to unlock actionable insights that drive operational and strategic decisions.',
      'Strengthen data security and AI system protections to safeguard business operations and customer information.',
    ],
  },
  {
    name: 'Administration',
    title: 'Achieve AI-Powered Administration Excellence',
    points: [
      'Automate repetitive administrative tasks with AI, freeing up resources for strategic initiatives and increasing efficiency.',
      'Implement AI-powered solutions for smarter financial forecasting, risk management, and compliance tracking.',
      'Use AI to measure and optimize corporate performance, ensuring alignment with strategic business goals and improving overall productivity.',
    ],
  },
  {
    name: 'Operations',
    title: 'Streamline Operations with AI',
    points: [
      'Streamline operations by automating tasks and eliminating inefficiencies, reducing costs and increasing throughput.',
      'Implement AI-driven operational analytics to gain real-time insights and improve decision-making across business processes.',
      'Ensure continuous improvement by leveraging AI tools to identify and address bottlenecks, ensuring smooth operations and rapid ROI.',
    ],
  },
  {
    name: 'Customer',
    title: 'Deepen Customer Understanding with AI',
    points: [
      'Use AI-driven insights to create highly personalized customer interactions, enhancing engagement and loyalty.',
      'Utilize AI to analyze customer data for deeper understanding and more effective customer relationship management.',
      'Integrate generative AI technologies to automate and personalize customer experiences at scale, improving both service and satisfaction.',
    ],
  },
]

export const aiImpact: WrittenPage = {
  navLabel: 'AI Impact',
  title: 'Stop chasing AI trends. Start proving impact.',
  description:
    'Every week, a new AI tool hits the market—another shiny solution, another promise. But despite the noise, 80% of AI initiatives stall before delivering real',
  sections: [
    // 1. The hero — out of page.html, since content.md does not carry it.
    {
      kind: 'hero',
      // Fatih, 2026-10-02: "Try" → "Discover" — no trial is on offer.
      eyebrow: 'Discover Impact Platform',
      headline: 'Stop Chasing AI Trends. Start Proving Impact.',
      sub: 'AI budgets are exploding. But most initiatives fail to scale. Teams get lost in trends and noise.',
      body: [
        'With Digitopia, you can cut through the hype, clarify your strategy, and focus on what truly creates value.',
      ],
      // D.1.1: was "Start Free Trial" → the page's own trial form.
      ctas: [{ label: 'Request a Demo', href: '#contact' }],
      photo: {
        src: '/photo/boardroom-impact-platform-applause.webp',
        alt: 'A meeting room applauding a score presented on the Impact Platform, the benchmark chart on the screen behind the table.',
      },
    },
    // 2. The customer row: eight marks, in the old page's order, every one of
    //    them a file the wall already uses. Yazaki is not on the wall; its only
    //    mark is the story card's padded square, drawn the way the AIMI page
    //    draws it.
    {
      kind: 'logos',
      heading: 'JOIN THOUSANDS OF PIONEERS...',
      items: [
        { name: 'Aviva', src: '/logo/aviva.webp' },
        { name: 'Bridgestone', src: '/logo/bridgestone.webp' },
        { name: 'Carlsberg Group', src: '/logo/carlsberggroup.webp' },
        { name: 'DHL', src: '/logo/dhl.webp' },
        { name: 'Netafim', src: '/logo/netafim.webp' },
        { name: 'Unilever', src: '/logo/unilever.webp' },
        { name: 'Yazaki', src: '/logo/yazakicard.png', padded: true },
        { name: 'Vodafone', src: '/logo/vodafone.webp' },
      ],
    },
    // 3. Why AI Fails to Deliver Value — the picture on the left, as on the old page.
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Why AI Fails to Deliver Value',
      body: [
        'Every week, a new AI tool hits the market—another shiny solution, another promise. But despite the noise, 80% of AI initiatives stall before delivering real outcomes. Why?',
        'Because organizations:',
      ],
      points: [
        'Chase trends instead of setting clear priorities',
        'Struggle to align strategy, people, and technology',
        'Can’t measure or communicate the value they’re creating',
      ],
      outro: [
        'Without structure, clarity, and proof of progress, AI becomes just another failed experiment.',
      ],
      photo: {
        src: '/photo/man-frustrated-at-laptop.webp',
        alt: 'A man at a laptop with his hand over his face, framed by a curved brand-gradient line.',
      },
      // D.1.1: was "Try the Impact Platform for Free" → the page's own trial form.
      ctas: [{ label: 'Request a Demo', href: '#contact' }],
    },
    // 4. Digitopia is Your GPS in the AI World. Two headings in a row, as
    //    content.md has them; the second sits tight under the first.
    {
      kind: 'prose',
      align: 'center',
      heading: 'Gain the Most Value from Artificial Intelligence:',
    },
    {
      kind: 'prose',
      align: 'center',
      tightTop: true,
      size: 'lg',
      heading: 'Digitopia is Your GPS in the AI World',
      paragraphs: [
        'Digitopia empowers organizations to achieve their AI transformation goals by providing the right strategy and tools. Assess, benchmark, and build an enterprise-grade AI strategy—fast, scalable, and aligned to your business goals.',
      ],
    },
    {
      kind: 'photo',
      photo: {
        src: '/photo/team-reviewing-aimi-benchmark.webp',
        alt: 'Three colleagues at a wall screen reviewing an AIMI score, the scores by dimension and an industry benchmark on the Impact Platform.',
      },
    },
    {
      kind: 'features',
      variant: 'rows',
      items: [
        {
          title: 'AI Maturity Assessment and Benchmarking',
          lines: [
            'A structured AI maturity checkup across 6 strategic dimensions',
            'Real-time benchmarking against peers in your industry',
          ],
        },
        {
          title: 'AI-Powered Recommendations and Roadmap',
          lines: [
            'Instant, AI-powered recommendations tailored to your context',
            'A clear, actionable roadmap based on what drives real impact',
          ],
        },
        {
          title: 'Progress Tracking and Data Insights',
          lines: [
            'Executive-level dashboards to track progress and report results',
            'Focus on what creates real value—not just what’s trending',
          ],
        },
      ],
    },
    // 5. Capture AI Value — the four reasons, then the picture that sat beside them.
    {
      kind: 'features',
      heading: 'Capture AI Value',
      columns: 2,
      items: [
        {
          title: 'Enhance Your AI Readiness',
          body: 'Address critical gaps, including data quality and talent, to fully realize the value of AI for your organization.',
        },
        {
          title: 'Maximize ROI on AI Investments',
          body: 'Prioritize high-impact AI projects and optimize your budget so that every investment delivers significant returns and boosts stakeholder confidence.',
        },
        {
          title: 'Catch Up with AI’s Speed',
          body: 'Deliver tangible business value and measurable outcomes within months, accelerating the transition from AI pilots to enterprise-wide adoption.',
        },
        {
          title: 'Cut Through the Noise',
          body: 'Select the right technologies that propel your AI strategy forward, eliminating complexity and keeping you focused on your AI objectives.',
        },
      ],
    },
    {
      kind: 'photo',
      photo: {
        src: '/photo/aimi-assessment-presentation.webp',
        alt: 'A consultant presenting an AIMI score, the scores by dimension and an industry benchmark to colleagues on a meeting-room screen.',
      },
    },
    // 6. The figures. D.1.2: the old 100% Success Rate / 15+ Industries Served /
    //    35+ Countries Covered tiles are not carried over. Fatih chose (a) on
    //    2026-10-02: the company-reach set the Home page's logo band prints, in
    //    its order. Not the DAIMI study's 1,500+/20+/10+ nor the DAIMI page's
    //    1,300/20+ — those are listed for him separately and are not this page's.
    {
      kind: 'stats',
      items: [
        { figure: '300+', label: 'customers' },
        { figure: '40+', label: 'countries' },
        { figure: '20+', label: 'industries' },
        { figure: '3,000+', label: 'measurements' },
      ],
    },
    // 7. The AIMI framework and its six dimensions — the site's current wheel,
    //    then one card per dimension (D.1.3), stacked like the AIMI page's cards.
    {
      kind: 'scale',
      heading: 'Achieve Success in Your AI Transformation',
      intro:
        'Leverage the Artificial Intelligence Maturity Index (AIMI) framework to advance your AI transformation with the right strategy and fully capture the value AI brings to your organization. Our methodology is backed by years of transformation insight and industry benchmarking. The AI Maturity Index (AIMI) evaluates your organization across six dimensions that drive success:',
      photo: {
        src: '/photo/aimi-framework-wheel.webp',
        alt: 'The AIMI wheel: Capabilities, Governance, Technology, Customer, Operations and Administration around the Artificial Intelligence Maturity Index, with the topics measured under each.',
      },
    },
    ...DIMENSIONS.map(
      (d): WrittenPage['sections'][number] => ({
        kind: 'cta',
        tone: 'card',
        heading: d.name,
        sub: d.title,
        points: d.points,
        ctas: [],
      }),
    ),
    // 8. PROVEN — the Hall of Fame card, as the AIMI page draws it, with the
    //    quote this page carries (it is not the one the AIMI page uses).
    {
      kind: 'quotes',
      variant: 'fame',
      items: [
        {
          quote:
            'Everyone wanted to ‘do something with AI,’ but we had no way to prioritize or prove value. Digitopia helped us benchmark our capabilities, define what matters, and present a clear roadmap to leadership. For the first time, AI became a measurable business initiative.',
          source: 'Johann Heaven, CEO, PROVEN',
          company: 'PROVEN',
          logo: { name: 'PROVEN', src: '/logo/provencard.png' },
          portrait: { src: '/photo/portrait-johann-heaven.png', alt: 'Johann Heaven' },
        },
      ],
    },
    // 9. The closing ask. Its form is the shared contact band directly under
    //    it (`#contact`), so it carries the words and no button (D.1.1).
    {
      kind: 'prose',
      align: 'center',
      size: 'lg',
      heading: 'Stop Chasing Trends. Start Proving Value.',
      paragraphs: [
        'AI is too important to leave to guesswork.',
        // Fatih, 2026-10-02: "Try" → "Discover", this word only.
        'Discover the Impact Platform that brings clarity, direction, and measurable outcomes to your AI strategy.',
      ],
    },
  ],
}
